"use client";
/**
 * MessageSearch — sticky search control for the Ask chat area.
 *
 * Pinned top-right of the chat panel. Click the search icon to expand a
 * field that searches INSIDE the current conversation's messages.
 * Each matched word is highlighted inside the message content.
 * Prev/Next jump between messages containing matches.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronUp, ChevronDown } from "lucide-react";
import type { Message } from "@/modes/ask/types";

export interface MessageSearchState {
  open:    boolean;
  query:   string;
  /** Message-array indexes that contain at least one occurrence */
  matches: number[];
  /** Index of the active occurrence in the flat occurrence list (0-based) */
  active:  number | null;
  /** Message-array index of the active occurrence (drives the ring) */
  activeMessage: number | null;
  /** 0-based ordinal of the active occurrence WITHIN its message */
  activeOccurrence: number | null;
  /** Total occurrences of the query across ALL messages */
  totalOccurrences: number;
  /** Bumped on every navigation so the parent can replay the flash */
  navCount: number;
}

interface MessageSearchProps {
  messages: Message[];
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  onStateChange: (state: MessageSearchState) => void;
  /**
   * Query handed over from a sidebar search hit via /ask/{id}?find=... — the
   * panel opens pre-filled and jumps straight to the first occurrence.
   */
  initialQuery?: string;
}

/** Content values that should never count as search results. */
const IGNORED_CONTENT = new Set(["", "Thinking...", "Loading..."]);

function isSearchable(msg: Message): boolean {
  if (msg.type === "upgrade") return false;
  if (msg.role !== "user" && msg.role !== "assistant") return false;
  const text = (msg.content ?? "").trim();
  return !IGNORED_CONTENT.has(text);
}

/**
 * Count how many times `sub` appears in `str` (case-insensitive).
 */
function countOccurrences(str: string, sub: string): number {
  let count = 0;
  let pos   = 0;
  while ((pos = str.indexOf(sub, pos)) !== -1) {
    count++;
    pos += sub.length;
  }
  return count;
}

/** A single occurrence of the query: message index + ordinal within that message. */
interface Occurrence {
  msg: number;
  occ: number;
}

export function MessageSearch({ messages, scrollContainerRef, onStateChange, initialQuery }: MessageSearchProps) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState("");
  // `active` = index into the FLAT occurrence list (Chrome-proof: every word,
  // not just one per message).
  const [active,    setActive]    = useState<number | null>(null);
  const [navCount,  setNavCount]  = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const goToRef   = useRef<(direction: 1 | -1) => void>(() => {});
  const closeRef  = useRef<() => void>(() => {});

  // ── Compute matches + the flat per-occurrence list ──────────────────────

  const { messageMatches, occurrenceList } = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { messageMatches: [] as number[], occurrenceList: [] as Occurrence[] };
    const messageMatches: number[] = [];
    const occurrenceList: Occurrence[] = [];
    messages.forEach((msg, i) => {
      if (!isSearchable(msg)) return;
      const content = msg.content.toLowerCase();
      const n = countOccurrences(content, q);
      if (n > 0) {
        messageMatches.push(i);
        for (let o = 0; o < n; o++) occurrenceList.push({ msg: i, occ: o });
      }
    });
    return { messageMatches, occurrenceList };
  }, [messages, query]);

  // ── Publish state to parent (drives bubble highlighting) ───────────────

  const activeOcc = active != null ? occurrenceList[active] ?? null : null;

  useEffect(() => {
    onStateChange({
      open,
      query,
      matches: messageMatches,
      active,
      activeMessage: activeOcc?.msg ?? null,
      activeOccurrence: activeOcc?.occ ?? null,
      totalOccurrences: occurrenceList.length,
      navCount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, messageMatches, occurrenceList, active, activeOcc, navCount]);

  // ── Close → clear everything ────────────────────────────────────────────

  const close = () => {
    setOpen(false);
    setQuery("");
    setActive(null);
  };

  // ── Jump to an occurrence (centered in the chat area) ──────────────────
  // Follows the EXACT <mark> of that occurrence so the view walks through
  // every word, even multiple occurrences inside one long message.

  const scrollToMatch = (msgIndex: number, occInMsg: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLElement>(`[data-search-index="${msgIndex}"]`);
    if (!el) return;
    const marks = Array.from(el.querySelectorAll<HTMLElement>("mark"));
    const targetEl = marks[occInMsg] ?? marks[marks.length - 1] ?? el;
    const crTop  = container.getBoundingClientRect().top;
    const tTop   = targetEl.getBoundingClientRect().top;
    const desired = container.scrollTop + (tTop - crTop) - container.clientHeight / 2;
    const max     = Math.max(0, container.scrollHeight - container.clientHeight);
    const clamped = Math.min(Math.max(0, desired), max);
    if (Math.abs(clamped - container.scrollTop) >= 1) {
      container.scrollTo({ top: clamped, behavior: "auto" });
    }
    // Verify: if the word is still out of the visible range a frame later,
    // fall back to the browser finding the correct scroller.
    requestAnimationFrame(() => {
      const cr = container.getBoundingClientRect();
      const tb = targetEl.getBoundingClientRect();
      if (tb.top < cr.top + 8 || tb.bottom > cr.bottom - 8) {
        targetEl.scrollIntoView({ block: "center", behavior: "auto" });
      }
    });
  };

  const goTo = (direction: 1 | -1) => {
    const total = occurrenceList.length;
    if (total === 0) return;
    const cur  = active ?? (direction === 1 ? -1 : total);
    const next = (cur + direction + total) % total;
    setActive(next);
    setNavCount((c) => c + 1);
    const o = occurrenceList[next];
    scrollToMatch(o.msg, o.occ);
    // Keep the query field focused so repeated Enter / Arrow keys keep working,
    // even right after clicking the prev/next buttons.
    inputRef.current?.focus();
  };

  // Keep always-fresh references for the document-level key handler, so it
  // navigates with the LATEST matches/active even if focus left the input.
  useEffect(() => {
    goToRef.current  = goTo;
    closeRef.current = close;
  });

  // ── Handle query input ──────────────────────────────────────────────────

  const onQueryChange = (value: string) => {
    setQuery(value);
    // Chrome-style live jump: for the NEW query, immediately ring + jump to
    // the FIRST occurrence (no waiting for a re-render). Enter/arrows then
    // step through every remaining occurrence.
    const q = value.trim().toLowerCase();
    if (!q) {
      setActive(null);
      return;
    }
    let firstMsg: number | null = null;
    messages.forEach((msg, i) => {
      if (firstMsg != null) return;
      if (!isSearchable(msg)) return;
      if (countOccurrences(msg.content.toLowerCase(), q) > 0) firstMsg = i;
    });
    setActive(firstMsg != null ? 0 : null);
    requestAnimationFrame(() => {
      if (firstMsg != null) scrollToMatch(firstMsg, 0);
    });
  };

  // Autofocus input when expanding.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // ── Deep link (?find=term) — open pre-filled and jump to the first hit ──
  // Runs once, and only after the conversation has actually loaded, otherwise
  // there is nothing rendered to scroll to yet.

  const appliedInitialRef = useRef(false);
  useEffect(() => {
    const q = (initialQuery ?? "").trim();
    if (!q || appliedInitialRef.current) return;
    if (messages.length === 0) return;
    appliedInitialRef.current = true;
    // Deferred a frame so the conversation is laid out before we scroll.
    const raf = requestAnimationFrame(() => {
      setOpen(true);
      onQueryChange(q);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery, messages.length]);

  // ── Browser-style Cmd/Ctrl+F shortcut ──────────────────────────────────
  // Intercept the native find while the chat is mounted, so the in-chat
  // search opens instead of the browser find bar.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setOpen(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // ── Keep navigation working from ANYWHERE while search is open ─────────
  // Chrome's find bar steps matches no matter where focus is. Once our in-chat
  // search is open, Enter / ↓ / ↑ / Esc navigate matches even if the user
  // clicked on a message or elsewhere and the input lost focus. The chat
  // composer and the search input itself are never hijacked.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // Never hijack typing in the chat composer, the search input (its own
      // handler covers those), or any other field.
      if (tag === "TEXTAREA" || tag === "INPUT") return;
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault();
        goToRef.current(1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        goToRef.current(-1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const ordinal = active != null ? active + 1 : 0;
  const hasQuery = !!query.trim();

  return (
    <div className="absolute top-3 right-3 z-50">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search chat history"
          title="Search chat history"
          className="h-9 w-9 rounded-xl border border-border bg-card text-muted-foreground shadow-sm flex items-center justify-center transition-colors hover:bg-accent hover:text-foreground"
        >
          <Search className="h-4 w-4" />
        </button>
      ) : (
        <div className="w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-lg p-2 space-y-2">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); goTo(1); }
                if (e.key === "ArrowDown") { e.preventDefault(); goTo(1); }
                if (e.key === "ArrowUp") { e.preventDefault(); goTo(-1); }
                if (e.key === "Escape") { e.preventDefault(); close(); }
              }}
              placeholder="Search in this conversation…"
              className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
              aria-label="Search chat history"
            />
            <button
              type="button"
              onClick={close}
              aria-label="Close search"
              className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center justify-between border-t border-border/60 pt-2">
            <span className="text-xs text-muted-foreground font-mono select-none">
              {hasQuery
                ? occurrenceList.length > 0
                  ? `${ordinal} / ${occurrenceList.length}`
                  : "No matches"
                : "Type to search"}
            </span>
            <div className="flex items-center gap-1 relative z-10">
              <button
                type="button"
                onClick={() => goTo(-1)}
                onMouseDown={(e) => e.preventDefault()}
                disabled={occurrenceList.length === 0}
                aria-label="Previous match"
                title="Previous match"
                className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground disabled:opacity-30 hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => goTo(1)}
                onMouseDown={(e) => e.preventDefault()}
                disabled={occurrenceList.length === 0}
                aria-label="Next match"
                title="Next match"
                className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground disabled:opacity-30 hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
